import { Product } from 'src/app/models/core/product.model';
import { formatCurrency } from 'src/app/utils/currency-format';
import { getUnitPrice, hasPrice } from 'src/app/utils/product-price';

export type ProductDetailValue =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'currency'; readonly amount: number }
  | { readonly kind: 'translated'; readonly key: string; readonly params?: Readonly<Record<string, string | number>> }
  | { readonly kind: 'date-range'; readonly from: string; readonly to: string };

export interface ProductDetailRow {
  readonly id: 'price' | 'category' | 'min-age' | 'sale-limit' | 'sale-period';
  readonly labelKey: string;
  readonly value: ProductDetailValue;
}

/** Comisión del vendedor con formato de moneda o porcentaje; null si el producto no tiene. */
export function formatCommission(product: Pick<Product, 'comision' | 'comision_type'>): string | null {
  const { comision, comision_type } = product;
  if (comision == null || comision_type == null) return null;
  if (comision_type === 'fixed') return formatCurrency(comision);
  if (comision_type === 'percentage') return `${comision}%`;
  return null;
}

/** El backend manda la categoría a veces como objeto `{ name }` y a veces como texto. */
function categoryName(category: Product['category']): string | null {
  if (typeof category === 'string') return category || null;
  return category?.name ?? null;
}

function buildSaleLimitRow(product: Product): ProductDetailRow | null {
  if (!(product.limit_quote > 0)) return null;
  return { id: 'sale-limit', labelKey: 'product-sheet.sale-limit', value: { kind: 'text', text: String(product.limit_quote) } };
}

function buildSalePeriodRow(product: Product): ProductDetailRow | null {
  const { sale_initial_date: from, sale_finish_date: to } = product;
  if (!from || !to) return null;
  return { id: 'sale-period', labelKey: 'product-sheet.sale-period', value: { kind: 'date-range', from, to } };
}

function buildPriceRow(product: Product): ProductDetailRow {
  return {
    id: 'price',
    labelKey: 'product-sheet.price',
    value: hasPrice(product)
      ? { kind: 'currency', amount: getUnitPrice(product) }
      : { kind: 'translated', key: 'top-products.noCharge' },
  };
}

/** Filas de "Características" del sheet de producto, en orden y sin las que no tienen dato. */
export function buildProductDetailRows(product: Product): ProductDetailRow[] {
  const category = categoryName(product.category);

  const rows: (ProductDetailRow | null)[] = [
    buildPriceRow(product),
    category ? { id: 'category', labelKey: 'product-sheet.category', value: { kind: 'text', text: category } } : null,
    product.min_age
      ? { id: 'min-age', labelKey: 'product-sheet.min-age', value: { kind: 'translated', key: 'detail-product.min_age_text', params: { age: product.min_age } } }
      : null,
    buildSaleLimitRow(product),
    buildSalePeriodRow(product),
  ];

  return rows.filter((row): row is ProductDetailRow => row !== null);
}
